import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  getInvitationData,
  acceptFamilyInvitation,
} from '../domain/services/FamilyInvitationService';
import { signIn } from '../domain/services/auth/authService';
import { useUser } from '../contexts/UserContext';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';
import {
  FaGoogle,
  FaArrowRight,
  FaShieldAlt,
  FaChartLine,
  FaUniversity,
  FaUsers,
  FaBrain,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import logo_icon_64 from '../assets/images/logo-icon-64.png';
import { FamilyInvitation } from '../domain/types/FamilyInvitation';

const AcceptInvitationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, appUser } = useUser();

  const [invitationValid, setInvitationValid] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [invitationData, setInvitationData] = useState<FamilyInvitation>();
  const [emailMatch, setEmailMatch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginAttempted, setLoginAttempted] = useState(false);

  // Check if there is an invitationId in the URL parameters
  useEffect(() => {
    const checkInvitation = async () => {
      const params = new URLSearchParams(location.search);
      const invitationId = params.get('invitation_id');

      if (!invitationId) {
        setInvitationMessage('Invalid invitation link.');
        setInvitationValid(false);
        setLoading(false);
        return;
      }

      // Validate the invitationId
      try {
        const invitationData = await getInvitationData(invitationId);
        if (invitationData.status === 'pending') {
          setInvitationData(invitationData);
          setInvitationValid(true);
        } else {
          setInvitationMessage('This invitation is no longer valid.');
          setInvitationValid(false);
        }
      } catch (error) {
        console.error('Error validating invitation:', error);
        setInvitationMessage('Error validating invitation.');
        setInvitationValid(false);
      }
      setLoading(false);
    };

    checkInvitation();
  }, [location]);

  // After the user logs in, check if their email matches the invited email
  useEffect(() => {
    if (loginAttempted && currentUser && invitationData) {
      if (currentUser.email === invitationData.invitedEmail) {
        setEmailMatch(true);
        setInvitationMessage('');
      } else {
        setEmailMatch(false);
        setInvitationMessage(
          `Logged in as ${currentUser.email}, but this invitation is for ${invitationData}. Please log in with the correct account.`
        );
      }
    }
  }, [currentUser, invitationData, loginAttempted]);

  // Event handler for logging in
  const handleLogin = () => {
    signIn({
      forceNewAppUserId: true,
      familyId: invitationData?.familyId,
    }).then(() => {
      setLoginAttempted(true);
    });
  };

  // Event handler for accepting the invitation
  const handleAcceptInvitation = async () => {
    const params = new URLSearchParams(location.search);
    const invitationId = params.get('invitation_id');

    if (!invitationId) {
      setInvitationMessage('Invalid invitation ID.');
      return;
    }

    if (!appUser) {
      setInvitationMessage('User is not logged in.');
      return;
    }

    try {
      await acceptFamilyInvitation(invitationId, appUser);
      setInvitationMessage('You have successfully joined the family!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setInvitationMessage('Failed to accept the invitation.');
    }
  };

  // Render logic based on loading and error states
  if (loading) {
    return <LoadingState message="Loading invitation..." />;
  }

  if (!invitationValid) {
    return (
      <ErrorState
        error={new Error(invitationMessage || 'Invalid invitation')}
        title="Invitation Error"
      />
    );
  }

  // Render the component
  return (
    <>
      <Helmet>
        <title>Accept Invitation - OmniButler</title>
      </Helmet>
      <section className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <div className="max-w-4xl w-full mx-auto p-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <Link to="/">
              <img src={logo_icon_64} className="mx-auto h-16 w-16 mb-4" alt="OmniButler Logo" />
            </Link>
            <h1 className="text-3xl font-bold mb-2">You&apos;re Invited!</h1>
            <p className="text-slate-600 dark:text-slate-300">
              Join your family on OmniButler and simplify your finances together.
            </p>
          </div>

          {/* Content Section */}
          <div className="text-center">
            {/* Show initial message and login button if login hasn't been attempted */}
            {!loginAttempted && (
              <>
                <p className="mb-6">
                  To accept the invitation, please log in with your Google account associated with{' '}
                  <strong>{invitationData?.invitedEmail}</strong>.
                </p>
                <button
                  className="w-full py-3 px-5 inline-flex justify-center items-center font-semibold tracking-wide border align-middle duration-500 text-base text-center bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700 text-white rounded-lg"
                  onClick={handleLogin}
                >
                  <FaGoogle className="mr-2" /> Continue with Google
                </button>
              </>
            )}

            {/* Show accept button if emails match */}
            {loginAttempted && emailMatch && currentUser && (
              <div className="mt-6">
                <p className="mb-4">
                  Welcome, <strong>{currentUser.displayName}</strong>! Click the button below to
                  accept the invitation and join your family&apos;s financial dashboard.
                </p>
                <button
                  className="w-full py-3 px-5 inline-flex justify-center items-center font-semibold tracking-wide border align-middle duration-500 text-base text-center bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 text-white rounded-lg"
                  onClick={handleAcceptInvitation}
                >
                  <FaArrowRight className="mr-2" /> Accept Invitation
                </button>
              </div>
            )}

            {/* Show mismatch message if emails don't match */}
            {loginAttempted && !emailMatch && (
              <div className="mt-4 text-red-600">{invitationMessage}</div>
            )}
          </div>

          {/* Additional Information Section */}
          <section className="mt-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Why Join OmniButler?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Key Features */}
              <div className="p-6 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Key Features</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="w-6 h-6 text-indigo-600 mr-3">
                      <FaBrain />
                    </span>
                    <span>
                      <strong>Smart Account Syncing:</strong> Intelligent connection across all your
                      accounts.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <FaShieldAlt className="w-6 h-6 text-indigo-600 mr-3" />
                    <span>
                      <strong>Bank-Level Security:</strong> Your data is encrypted and never stored.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <FaChartLine className="w-6 h-6 text-indigo-600 mr-3" />
                    <span>
                      <strong>Real-Time Tracking:</strong> Live updates across all accounts.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <FaUniversity className="w-6 h-6 text-indigo-600 mr-3" />
                    <span>
                      <strong>Major US Banks:</strong> Connect with over 11,000 institutions.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <FaUsers className="w-6 h-6 text-indigo-600 mr-3" />
                    <span>
                      <strong>Family Access:</strong> Secure separate logins for you and your
                      spouse.
                    </span>
                  </li>
                </ul>
              </div>
              {/* Common Questions */}
              <div className="p-6 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Common Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold">How secure is my financial data?</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      We use bank-level encryption and never store your credentials. All data is
                      encrypted using AES-256, the same security standard used by major financial
                      institutions.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold">Which banks do you support?</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      We support major banks across the US, Canada, UK, and Australia through our
                      partnership with Plaid, including Chase, RBC, Barclays, Commonwealth Bank, and
                      thousands more.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold">Can I add my spouse to the account?</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Yes! Your spouse gets their own secure login while sharing access to your
                      consolidated financial dashboard.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold">How long does setup take?</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Most families connect their accounts in under 2 minutes. Simply sign in with
                      Google and connect your banks through our secure Plaid integration.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>
    </>
  );
};

export default AcceptInvitationPage;
